import { fork, put, race, select, take, takeLatest } from 'redux-saga/effects';

import { apiCall, isTokenExpired } from '../../utils';
import * as loginModule from './login';
import * as refreshModule from './refresh';
import * as tokensModule from './tokens';
import {actions as uiActions} from '../ui/actions';

// Helpers provided by auth modules
export function takeLogout(gen, ...args) {
  return takeLatest(
    [loginModule.types.LOGOUT, loginModule.types.SWITCH_SESSION],
    gen,
    ...args,
  );
}

function* takeAuthSaga(gen, ...args) {
  const { SET_TOKENS, CLEAR_TOKENS } = tokensModule.types;

  while (true) {
    const accessToken = yield select(state => state.auth.tokens.access);
    const catchedAction = yield take([SET_TOKENS, CLEAR_TOKENS]);

    if (
      catchedAction.type === SET_TOKENS &&
      !accessToken &&
      catchedAction.tokens.access
    ) {
      yield fork(gen, ...args);
    }
  }
}

export function takeAuth(gen, ...args) {
  return fork(takeAuthSaga, gen, args);
}

export function* authApiCall(config) {
  try {
    const { authorized, tokens } = yield select(state => state.auth);

    if (!authorized) {
      return yield apiCall(config);
    }

    // Access token expiration timestamp in microsecconds.
    const accessTokenExp = tokens.accessDecoded.exp;
    const accessTokenExpired = isTokenExpired(accessTokenExp);

    if (accessTokenExpired) {
      yield put(refreshModule.actions.refreshAuth());

      const { failure } = yield race({
        success: take(refreshModule.types.REFRESH_AUTH_SUCCESS),
        failure: take(refreshModule.types.REFRESH_AUTH_FAILURE),
        // TODO: Could be added timeout here to not block execution
        // in case of REFRESH_AUTH not responding(or not implemented).
      });

      if (failure) {
        yield put(loginModule.actions.logout());

        // Bypassing auth.
        return yield apiCall(config);
      }
    }

    const accessToken = yield select(state => state.auth.tokens.access);

    const response = yield apiCall({
      ...config,
      headers: { ...config.headers, Authorization: `Bearer ${accessToken}` },
    });

    return response;
  } catch (error) {
    // A 401/403 means the visitor hit an action that requires login —
    // either they were never authorized (guest), or they just became
    // unauthorized via the failed-refresh branch above (session expired).
    // Broadcast it globally instead of leaving each duck's own try/catch
    // to fail silently/differently: this is the one place every
    // customer-facing call passes through, so it's the one place that
    // can catch ALL of them without touching every screen/handler.
    const status = error?.response?.status;
    if (status === 401 || status === 403) {
      const { authorized: authorizedNow } = yield select(state => state.auth);
      if (!authorizedNow) {
        yield put(uiActions.showLoginPrompt());
      }
    }

    // Re-throw so existing per-module *_FAILURE handling still runs
    // unchanged — this is purely additive, nothing existing is removed.
    throw error;
  }
}